import {
	ChangeDetectionStrategy,
	ChangeDetectorRef,
	Component,
	DestroyRef,
	Input,
	OnChanges,
	SimpleChanges,
	Inject,
	Optional,
	signal
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { LiveAnnouncer } from '@angular/cdk/a11y';
import { CommonModule } from '@angular/common';
import { MatTooltipModule } from '@angular/material/tooltip';
import { BadgeModule } from '@go-modules/components/badge/badge.module';
import { AiMarkerSetPanelComponent } from '@go-modules/components/ai-marker-set-panel/ai-marker-set-panel.component';
import { BadgeColors } from '@go-modules/components/badge/badge.constants';
import type { LlmDefinition } from '@go-modules/interfaces/llm-definition';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ActivityModel } from 'go-modules/models/activity/activity.schema';
import { ActivityFormService } from '../activity-form/activity-form.service';
import { SelectedService, selectedServiceToken } from 'go-modules/services/selected/selected.service';
import { GoModalService } from '@go-modules/services/go-modal/go-modal.service';
import { mediaToken } from 'go-modules/models/media/media.factory';
import { mediaPreviewToken } from 'go-modules/modals/media-preview/modal.factory';
import { ProductTier } from '@go-modules/interfaces/licenses/product-tier';
import { isDismissedModalResult } from '@go-modules/utilities';
import { ActivityEditAccessibilityAnnouncerService } from '../activity-edit-accessibility-announcer.service';
import { ACTIVITY_EDITOR_CONTEXT } from '@go-modules/components/dialogs/activity-preview-editor/activity-editor-context';
import type {
	ActivityEditorContext
} from '@go-modules/components/dialogs/activity-preview-editor/activity-editor-context';
import type { AiMarkersEntitlement } from '@go-modules/interfaces/ai-markers-entitlement';
import { DisabledInteractiveDirective } from '@go-modules/directives/disabled-interactive/disabled-interactive.directive';
import { AIMarkersDialogComponent } from '@go-modules/components/dialogs/ai-markers-dialog/ai-markers-dialog.component';
import { catchError, of, take } from 'rxjs';
import { UserService as NgxUserService } from '@go-modules/services/user/user.service';

@Component({
	selector: 'activity-edit-ai-markers-section',
	standalone: true,
	imports: [
		CommonModule,
		BadgeModule,
		AiMarkerSetPanelComponent,
		TranslateModule,
		MatTooltipModule,
		DisabledInteractiveDirective
	],
	templateUrl: './activity-edit-ai-markers-section.component.html',
	styleUrls: ['./activity-edit-ai-markers-section.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush
})
export class ActivityEditAiMarkersSectionComponent implements OnChanges {
	@Input() public activity: ActivityModel | null = null;

	public llmDefinitions: LlmDefinition[] = [];
	public aiTranscriptionLanguage: string | null = null;
	public readonly canPreviewAiPrompts: boolean = true;
	private initialLlmDefinitions: LlmDefinition[] = [];
	private initialAiTranscriptionLanguage: string | null = null;
	private initialAiPromptsEnabled: boolean = false;
	private readonly aiMarkersEntitlement = signal<AiMarkersEntitlement | null>(null);
	private readonly aiMarkersEntitlementResolved = signal(false);

	public readonly badgeColors = BadgeColors;

	constructor (
		private activityFormService: ActivityFormService,
		@Inject(selectedServiceToken) private selectedService: SelectedService,
		private goModalService: GoModalService,
		private translate: TranslateService,
		private liveAnnouncer: LiveAnnouncer,
		@Inject(mediaToken) private MediaModel,
		@Inject(mediaPreviewToken) private mediaPreviewModal,
		private cdr: ChangeDetectorRef,
		private destroyRef: DestroyRef,
		private ngxUserService: NgxUserService,
		@Optional() @Inject(ACTIVITY_EDITOR_CONTEXT) private editorContext: ActivityEditorContext | null,
		@Optional() private activityEditAccessibilityAnnouncer?: ActivityEditAccessibilityAnnouncerService
	) {}

	public ngOnChanges (changes: SimpleChanges): void {
		if (changes.activity?.currentValue) {
			this.initializeFromActivity();
			this.updateUserAiMarkersEntitlement();
		}
	}

	public get hasDefinitions (): boolean {
		return !!this.llmDefinitions?.length;
	}

	public shouldShowAiMarkerSection (): boolean {
		const template = this.activity?.template;
		if (!template || !template.isActive?.('ai_prompts_enabled')) {
			return false;
		}

		if (this.usesUserAiMarkersEntitlement()) {
			return true;
		}

		const license = this.getContextLicense();
		const salesforceLicense = license?.salesforce_license;

		if (!license || salesforceLicense?.may_tease) {
			return true;
		}

		return !!salesforceLicense?.ai_prompts_enabled;
	}

	public shouldShowAdvancedPlanBadge (): boolean {
		if (this.usesUserAiMarkersEntitlement()) {
			if (!this.aiMarkersEntitlementResolved()) {
				return false;
			}

			return this.aiMarkersEntitlement()?.can_use_ai_markers !== true;
		}

		const license = this.getContextLicense();
		if (!license) {
			return true;
		}
		const salesforceLicense = license.salesforce_license;
		if (!salesforceLicense) {
			return true;
		}

		return salesforceLicense.tier_name === ProductTier.Standard ||
			salesforceLicense.tier_name === null ||
			salesforceLicense.is_free_trial;
	}

	public isAiMarkersUnavailable (): boolean {
		if (this.usesUserAiMarkersEntitlement()) {
			return !this.aiMarkersEntitlementResolved();
		}

		return this.editorContext != null &&
			this.editorContext.group?.() == null &&
			this.editorContext.org?.() == null;
	}

	public openAiMarkers (): void {
		if (!this.activity || this.isAiMarkersUnavailable()) {
			return;
		}

		const activity = this.activity;
		const hadDefinitions = this.hasDefinitions;
		this.goModalService.open(AIMarkersDialogComponent, true, {
			data: {
				activity,
				llmDefinitions: structuredClone(this.llmDefinitions ?? []),
				aiTranscriptionLanguage: this.aiTranscriptionLanguage,
				...this.getEditorDialogContextData()
			}
		}).afterClosed()
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe((result) => {
				if (!result || isDismissedModalResult(result)) {
					return;
				}
				this.llmDefinitions = structuredClone(result.llm_definitions ?? []);
				this.aiTranscriptionLanguage = result.ai_transcription_language ?? null;
				this.updateAiControls(true);
				this.announceAiMarkersChange(hadDefinitions);
				this.cdr.markForCheck();
			});
	}

	public removeAiMarkerSet (): void {
		const hadDefinitions = this.hasDefinitions;
		this.llmDefinitions = [];
		this.aiTranscriptionLanguage = null;
		this.updateAiControls(true);
		this.announceAiMarkersChange(hadDefinitions);
		this.cdr.markForCheck();
	}

	public previewAiMarkers (): void {
		const media = this.MediaModel.model({
			media_status: this.MediaModel.READY,
			media_type: this.MediaModel.TYPE.DOCUMENT,
			filename: 'ai-preview.gif',
			media_url: 'https://staticassets.goreact.com/ai-preview.gif',
			title: this.translate.instant('activity-info_example-ai-markers')
		});

		this.mediaPreviewModal.open({
			modalData: {
				media,
				edit: false,
				allowDownload: false
			}
		});
	}

	public resetSelections (): void {
		this.llmDefinitions = structuredClone(this.initialLlmDefinitions ?? []);
		this.aiTranscriptionLanguage = this.initialAiTranscriptionLanguage ?? null;
		const resolvedState = this.updateAiControls(false, this.initialAiPromptsEnabled);
		this.initialLlmDefinitions = structuredClone(this.llmDefinitions ?? []);
		this.initialAiTranscriptionLanguage = this.aiTranscriptionLanguage;
		this.initialAiPromptsEnabled = resolvedState;
		this.cdr.markForCheck();
	}

	private initializeFromActivity (): void {
		if (!this.activity) {
			return;
		}

		this.llmDefinitions = structuredClone(this.activity.llm_definitions ?? []);
		this.aiTranscriptionLanguage = this.activity.ai_transcription_language ?? null;
		this.initialLlmDefinitions = structuredClone(this.llmDefinitions ?? []);
		this.initialAiTranscriptionLanguage = this.aiTranscriptionLanguage;
		this.initialAiPromptsEnabled = !!this.activity.ai_prompts_enabled || this.hasAiSelections();
		const resolvedState = this.updateAiControls(false, this.initialAiPromptsEnabled);
		this.initialAiPromptsEnabled = resolvedState;
		this.cdr.markForCheck();
	}

	private updateAiControls (markDirty: boolean, aiPromptsOverride?: boolean): boolean {
		const llmControl = this.activityFormService.getControl('llm_definitions');
		const languageControl = this.activityFormService.getControl('ai_transcription_language');
		const aiPromptsControl = this.activityFormService.getControl('ai_prompts_enabled');
		const llmDefinitionsClone = structuredClone(this.llmDefinitions ?? []);
		const shouldEnableAiPrompts = typeof aiPromptsOverride === 'boolean'
			? aiPromptsOverride
			: this.hasAiSelections();

		llmControl?.setValue(llmDefinitionsClone);
		languageControl?.setValue(this.aiTranscriptionLanguage);
		aiPromptsControl?.setValue(shouldEnableAiPrompts);

		if (markDirty) {
			llmControl?.markAsDirty();
			languageControl?.markAsDirty();
			aiPromptsControl?.markAsDirty();
			this.activityFormService.markFormAsDirty();
		} else {
			llmControl?.markAsPristine();
			languageControl?.markAsPristine();
			aiPromptsControl?.markAsPristine();
		}

		if (this.activity) {
			this.activity.llm_definitions = structuredClone(llmDefinitionsClone);
			this.activity.ai_transcription_language = this.aiTranscriptionLanguage;
			this.activity.ai_prompts_enabled = shouldEnableAiPrompts;
		}

		return shouldEnableAiPrompts;
	}

	private hasAiSelections (): boolean {
		return (this.llmDefinitions?.length ?? 0) > 0;
	}

	private updateUserAiMarkersEntitlement (): void {
		if (!this.usesUserAiMarkersEntitlement()) {
			this.aiMarkersEntitlement.set(null);
			this.aiMarkersEntitlementResolved.set(false);
			return;
		}

		if (this.aiMarkersEntitlementResolved()) {
			return;
		}

		this.aiMarkersEntitlement.set(null);
		this.aiMarkersEntitlementResolved.set(false);

		this.ngxUserService.getAiMarkersEntitlement()
			.pipe(
				take(1),
				catchError(() => of({
					can_use_ai_markers: false,
					license: null,
					llm_definitions: []
				})),
				takeUntilDestroyed(this.destroyRef)
			)
			.subscribe((entitlement) => {
				this.aiMarkersEntitlement.set(entitlement);
				this.aiMarkersEntitlementResolved.set(true);
				this.cdr.markForCheck();
			});
	}

	private usesUserAiMarkersEntitlement (): boolean {
		return this.editorContext?.environment?.()?.name === 'library';
	}

	private getContextLicense (): any {
		return this.editorContext != null
			? this.editorContext.license?.() ?? null
			: this.selectedService.getLicense();
	}

	private getEditorDialogContextData (): Record<string, unknown> {
		if (this.editorContext == null) {
			return {};
		}

		const contextData: Record<string, unknown> = {
			group: this.editorContext.group?.() ?? null,
			account: this.editorContext.account?.() ?? null,
			org: this.editorContext.org?.() ?? null,
			license: this.editorContext.license?.() ?? null
		};

		if (this.usesUserAiMarkersEntitlement() && this.aiMarkersEntitlementResolved()) {
			contextData.aiMarkersEntitlement = this.aiMarkersEntitlement();
		}

		return contextData;
	}

	private announceAiMarkersChange (hadDefinitions: boolean): void {
		if (!hadDefinitions && this.hasDefinitions) {
			this.announceAccessibilityMessage('activity-editor-new_ai-markers-added');
		} else if (hadDefinitions && !this.hasDefinitions) {
			this.announceAccessibilityMessage('activity-editor-new_ai-markers-removed');
		}
	}

	private announceAccessibilityMessage (messageKey: string): void {
		const message = this.translate.instant(messageKey);
		if (this.activityEditAccessibilityAnnouncer != null) {
			this.activityEditAccessibilityAnnouncer.announce(message);
			return;
		}

		void this.liveAnnouncer.announce(message, 'assertive');
	}
}
